import { Component, OnDestroy, OnInit, Input, inject, ViewChild, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, filter, pairwise, startWith } from 'rxjs/operators';
import { trigger, style, transition, animate } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { AuditService } from '../../../../services/audit.service';
import { KeyingAuthService } from '../../../../services/keying.auth.service';
import { MecsErrorsDialogComponent } from '../../../../dialogs/mecs-errors-dialog/mecs-errors-dialog.component';
import { User } from '@overtur-monorepo/keying-user';
import { 
  IAuditLogEntry, 
  IAuditLogDetail, 
  IAuditTrailResult, 
  IAuditFilters,
  IAuditOptions
} from '../../../../models/audit';


@Component({
  selector: 'app-audit-trail',
  templateUrl: './audit-trail.component.html',
  styleUrls: ['./audit-trail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: '0', opacity: 0 }),
        animate('300ms ease-in-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-in-out', style({ height: '0', opacity: 0 }))
      ])
    ]),
    trigger('slideDown', [
      transition(':enter', [
        style({ height: '0', opacity: 0 }),
        animate('200ms ease-in-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in-out', style({ height: '0', opacity: 0 }))
      ])
    ])
  ]
})
export class AuditTrailComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() structureId?: number;
  @Input() keySystemId?: number;
  @Input() entityId?: number;
  @Input() entityType?: string;

  @ViewChild(MatSort) sort!: MatSort;

  // Field display name transformations - add more mappings here as needed
  private readonly FIELD_DISPLAY_NAME_MAP: { [key: string]: string } = {
    'Key Section Id': 'KeySection',
    'Keyway Id': 'Keyway',
    'Requires Pinning Generation': 'Has Pinnings',
    'Is Published': 'Published',
    'Ready for MECS': 'MECS Ready',
    'Is System Generated': 'Is TMK Book',
    'Master Key Bitting': 'Bitting',
    'Is Assigned': 'Key Assigned',
    'Is Future Key': 'Future Key',
    'Is Omk System': 'Omk System',
    'Key Set Symbol': 'Symbol',
    'Top Master Key Name': 'TMK Name',
    'Is Dealer Key System': 'Dealer Key System',
    'Is Deleted': 'Deleted',
    'Is Dummy Top Master': 'Dummy TMK',
    'Is Previously Published': 'Previously Published',
    'Is Vinz Key System': 'Advanced Rekey System',
    'User Queue': 'Assigned To User',
    'Is Specified': 'Specified Key System'
    // Add more field name transformations here
  };

  // Data
  auditEntries: IAuditLogEntry[] = [];
  dataSource = new MatTableDataSource<IAuditLogEntry>([]);
  
  // Pagination
  currentPage = 0;
  pageSize = 10;
  totalCount = 0;
  pageSizeOptions = [10, 25, 50];

  // UI State
  isLoading = false;
  showAdvancedFilters = false;
  expandedItems = new Set<number>();

  // Table configuration
  allColumns = [
    { key: 'timestamp', label: 'DateTime', visible: true, required: true },
    { key: 'user', label: 'User', visible: true, required: true },
    { key: 'action', label: 'Action', visible: true, required: true },
    { key: 'entity', label: 'Record Name', visible: true, required: false },
    { key: 'actionDescription', label: 'Description', visible: true, required: false },
    { key: 'identifier', label: 'Identifier', visible: true, required: false },
    { key: 'keyway', label: 'Keyway', visible: true, required: false },
    { key: 'appName', label: 'App Name', visible: true, required: false },
    { key: 'source', label: 'Source', visible: false, required: false },
    { key: 'actions', label: 'Details', visible: true, required: true }
  ];
  
  get displayedColumns(): string[] {
    return this.allColumns.filter(col => col.visible).map(col => col.key);
  }
  
  detailsDisplayedColumns: string[] = ['fieldName', 'oldValue', 'newValue'];
  
  // Column visibility
  showColumnSelector = false;

  // Forms
  filterForm: FormGroup;
  
  // Available options for filters
  availableUsers: string[] = [];
  availableActions: string[] = [];
  availableEntityTypes: string[] = [];
  currentUserId?: number;

  // Quick filters
  quickFilters = {
    ALL_TIME: { label: 'All Time', active: true },
    WEEK: { label: 'This Week', active: false },
    LAST_MONTH: { label: 'Last Month', active: false },
    MY_CHANGES: { label: 'My Changes', active: false },
    CREATES_ONLY: { label: 'Create Only', active: false },
    DELETES_ONLY: { label: 'Delete Only', active: false },
    UPDATES_ONLY: { label: 'Update Only', active: false }
  };

  destroy$ = new Subject<boolean>();

  // Modern Angular injection pattern
  private readonly formBuilder = inject(FormBuilder);
  private readonly auditService = inject(AuditService);
  private readonly authService = inject(KeyingAuthService);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadColumnPreferences();
    this.setupCurrentUser();
    this.loadAllUsers();
    this.loadAuditData();
  }

  ngAfterViewInit() {
    // Configure custom sorting data accessor to map column keys to actual data properties
    this.dataSource.sortingDataAccessor = (item: IAuditLogEntry, property: string) => {
      switch (property) {
        case 'timestamp':
          return new Date(item.timestamp).getTime();
        case 'user':
          return item.userDisplayName?.toLowerCase() || '';
        case 'action':
          return item.action?.toLowerCase() || '';
        case 'entity':
          return item.entityDisplayName?.toLowerCase() || '';
        case 'actionDescription':
          return item.actionDisplayName?.toLowerCase() || '';
        case 'identifier':
          return item.identifier?.toLowerCase() || '';
        case 'keyway':
          return item.keyway?.toLowerCase() || '';
        case 'appName':
          return item.appName?.toLowerCase() || '';
        case 'source':
          return item.source?.toLowerCase() || '';
        default:
          return '';
      }
    };

    this.connectSort();
  }

  private connectSort(): void {
    // Connect MatSort to the dataSource - needs to be called after view init
    // and whenever data is updated (because table is in *ngIf)
    setTimeout(() => {
      if (this.sort) {
        this.dataSource.sort = this.sort;
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.filterForm = this.formBuilder.group({
      dateFrom: new FormControl(),
      dateTo: new FormControl(),
      users: new FormControl([]),
      userIds: new FormControl([]),
      actions: new FormControl([]),
      entityTypes: new FormControl([]),
      searchText: new FormControl(''),
      showSystemChanges: new FormControl(false)
    });

    // Watch entityTypes: Feature is mutually exclusive with all other types
    this.filterForm.get('entityTypes')?.valueChanges
      .pipe(
        startWith([] as string[]),
        pairwise(),
        takeUntil(this.destroy$)
      )
      .subscribe(([prev, curr]: [string[], string[]]) => {
        if (curr.includes('Feature') && curr.length > 1) {
          const featureJustAdded = !prev.includes('Feature');
          if (featureJustAdded) {
            // Feature was just added — deselect everything else
            this.filterForm.get('entityTypes')?.setValue(['Feature'], { emitEvent: false });
            this.updateAvailableActionsBasedOnEntityTypes(['Feature']);
          } else {
            // Something else was added while Feature was selected — remove Feature
            const updated = curr.filter(t => t !== 'Feature');
            this.filterForm.get('entityTypes')?.setValue(updated, { emitEvent: false });
            this.updateAvailableActionsBasedOnEntityTypes(updated);
          }
        } else {
          this.updateAvailableActionsBasedOnEntityTypes(curr);
        }
      });

    // Watch for changes to actions to update available entity types
    this.filterForm.get('actions')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedActions: string[]) => {
        this.updateAvailableEntityTypesBasedOnActions(selectedActions);
      });
  }



  private loadAuditData(): void {
    if (!this.structureId) {
      console.warn('No structureId provided for audit trail');
      return;
    }

    this.isLoading = true;
    
    this.auditService.getDefaultAuditTrail(this.structureId, {
      pageSize: this.pageSize,
      pageIndex: this.currentPage + 1,
      includeDetails: true
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result: IAuditTrailResult) => {
        this.auditEntries = result.auditLogs.map(entry => ({
          ...entry,
          actionIcon: this.getActionIcon(entry.action),
          actionColor: this.getActionColorForEntry(entry),
          isExpanded: false
        }));
        this.totalCount = result.totalCount;
        this.dataSource.data = [...this.auditEntries];
        this.connectSort(); // Reconnect sort after data update
        this.updateAvailableUsers();
        this.updateAvailableActions();
        this.updateAvailableEntityTypes();
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load audit data:', error);
        this.auditEntries = [];
        this.totalCount = 0;
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }


  private updateQuickFilterStates(): void {
    const filters: IAuditFilters = this.filterForm.value;
    const weekAgo = this.getWeekAgo();
    const startOfLastMonth = this.getStartOfLastMonth();
    const endOfLastMonth = this.getEndOfLastMonth();

    // Calculate new states
    const allTimeActive = !filters.dateFrom && !filters.dateTo;

    const weekActive = filters.dateFrom &&
      Math.abs(filters.dateFrom.getTime() - weekAgo.getTime()) < 24 * 60 * 60 * 1000 &&
      !filters.dateTo;

    const lastMonthActive = filters.dateFrom && filters.dateTo &&
      Math.abs(filters.dateFrom.getTime() - startOfLastMonth.getTime()) < 1000 &&
      Math.abs(filters.dateTo.getTime() - endOfLastMonth.getTime()) < 1000;

    const updatesOnlyActive = filters.actions?.length === 1 &&
      filters.actions[0] === 'Update';

    const createsOnlyActive = filters.actions?.length === 1 &&
      filters.actions[0] === 'Create';

    const deletesOnlyActive = filters.actions?.length === 1 &&
      filters.actions[0] === 'Delete';

    const myChangesActive = filters.userIds?.length === 1 &&
      filters.userIds[0] === this.currentUserId;

    let stateChanged = false;

    // Only update states that actually changed to prevent unnecessary animations
    if (this.quickFilters.ALL_TIME.active !== allTimeActive) {
      this.quickFilters.ALL_TIME.active = allTimeActive;
      stateChanged = true;
    }
    if (this.quickFilters.WEEK.active !== weekActive) {
      this.quickFilters.WEEK.active = weekActive;
      stateChanged = true;
    }
    if (this.quickFilters.LAST_MONTH.active !== lastMonthActive) {
      this.quickFilters.LAST_MONTH.active = lastMonthActive;
      stateChanged = true;
    }
    if (this.quickFilters.CREATES_ONLY.active !== createsOnlyActive) {
      this.quickFilters.CREATES_ONLY.active = createsOnlyActive;
      stateChanged = true;
    }
    if (this.quickFilters.DELETES_ONLY.active !== deletesOnlyActive) {
      this.quickFilters.DELETES_ONLY.active = deletesOnlyActive;
      stateChanged = true;
    }
    if (this.quickFilters.UPDATES_ONLY.active !== updatesOnlyActive) {
      this.quickFilters.UPDATES_ONLY.active = updatesOnlyActive;
      stateChanged = true;
    }
    if (this.quickFilters.MY_CHANGES.active !== myChangesActive) {
      this.quickFilters.MY_CHANGES.active = myChangesActive;
      stateChanged = true;
    }

    if (stateChanged) {
      this.cdr.markForCheck();
    }
  }

  // Table-specific methods
  isExpandableRow = (_index: number, row: IAuditLogEntry): boolean => row.hasDetails;

  private loadAllUsers(): void {
    // Get all users from auth service (like structure-card component)
    this.authService.users$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((users: User[]) => {
      if (!users) {
        this.authService.getUsers();
        return;
      }
      // Format users as "FirstName LastName" and sort
      this.availableUsers = users
        .map(user => `${user.firstName} ${user.lastName}`)
        .sort((a, b) => a.localeCompare(b));
      this.cdr.markForCheck();
    });
  }

  private updateAvailableUsers(): void {
    // This method is now just a placeholder since we load all users at startup
    // Keep for consistency with other update methods
  }

  private updateAvailableActions(): void {
    // Initialize with all actions - will be filtered if needed
    const selectedEntityTypes = this.filterForm.get('entityTypes')?.value || [];
    this.updateAvailableActionsBasedOnEntityTypes(selectedEntityTypes);
  }

  private updateAvailableActionsBasedOnEntityTypes(selectedEntityTypes: string[]): void {
    // If Structure is selected, exclude Delete action (deleted structures can't be viewed)
    if (selectedEntityTypes.includes('Structure')) {
      this.availableActions = ['Create', 'Update'];

      // Remove Delete from selected actions if it was previously selected
      const currentActions = this.filterForm.get('actions')?.value || [];
      if (currentActions.includes('Delete')) {
        const updatedActions = currentActions.filter(action => action !== 'Delete');
        this.filterForm.patchValue({ actions: updatedActions }, { emitEvent: false });
      }
    } else {
      this.availableActions = ['Create', 'Update', 'Delete'];
    }
  }

  private updateAvailableEntityTypes(): void {
    // Initialize with all entity types - will be filtered if needed
    const selectedActions = this.filterForm.get('actions')?.value || [];
    this.updateAvailableEntityTypesBasedOnActions(selectedActions);
  }

  private updateAvailableEntityTypesBasedOnActions(selectedActions: string[]): void {
    // If Delete is selected, exclude Structure (deleted structures can't be viewed)
    if (selectedActions.includes('Delete')) {
      this.availableEntityTypes = ['Book', 'Cylinder', 'Feature', 'Key', 'Key System'];

      // Remove Structure from selected entity types if it was previously selected
      const currentEntityTypes = this.filterForm.get('entityTypes')?.value || [];
      if (currentEntityTypes.includes('Structure')) {
        const updatedEntityTypes = currentEntityTypes.filter(type => type !== 'Structure');
        this.filterForm.patchValue({ entityTypes: updatedEntityTypes }, { emitEvent: false });
      }
    } else {
      this.availableEntityTypes = ['Book', 'Cylinder', 'Feature', 'Key', 'Key System', 'Structure'];
    }
  }

  private setupCurrentUser(): void {
    // Get current user ID from authentication service
    this.authService.keyingUser$
      .pipe(
        takeUntil(this.destroy$),
        filter(user => user !== null)
      )
      .subscribe((user: User) => {
        this.currentUserId = user.id;
        this.cdr.markForCheck();
      });
  }

  // Utility methods for date handling
  private getWeekAgo(): Date {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return weekAgo;
  }

  private getStartOfLastMonth(): Date {
    const now = new Date();
    const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    firstDayOfLastMonth.setHours(0, 0, 0, 0);
    return firstDayOfLastMonth;
  }

  private getEndOfLastMonth(): Date {
    const now = new Date();
    // Get first day of current month, then subtract 1 millisecond to get last moment of previous month
    const firstDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMomentOfLastMonth = new Date(firstDayOfCurrentMonth.getTime() - 1);
    return lastMomentOfLastMonth;
  }

  private convertToEndOfDay(date: Date | null | undefined): Date | null {
    if (!date) return null;
    
    // Create a new date to avoid modifying the original
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay;
  }


  // Search methods
  searchAuditData(): void {
    // Reset to first page when applying new filters
    this.currentPage = 0;
    this.loadAuditDataWithFilters();
  }

  private loadAuditDataWithFilters(): void {
    const filters = this.filterForm.value;
    const isFeatureSelected = filters.entityTypes?.includes('Feature');

    if (!isFeatureSelected && !this.structureId) {
      console.warn('No structureId provided for audit trail');
      return;
    }

    this.isLoading = true;

    // Map display names to backend entity type values
    const mappedEntityTypes = filters.entityTypes?.length ?
      filters.entityTypes.map(displayName => this.mapEntityTypeToBackend(displayName)) : undefined;

    // Map display action names to backend values
    const mappedActions = filters.actions?.length ?
      filters.actions.map(displayName => this.mapActionToBackend(displayName)) : undefined;

    // Only pass keySystemId when user specifically selects "Key System" entity type
    const needsKeySystemId = mappedEntityTypes?.includes('Key System');

    // Build options with all filter parameters for backend
    const options: IAuditOptions = {
      pageSize: this.pageSize,
      pageIndex: this.currentPage + 1,
      includeDetails: true,
      structureId: this.structureId,
      keySystemId: needsKeySystemId ? this.keySystemId : undefined,
      fromDate: filters.dateFrom?.toISOString(),
      toDate: this.convertToEndOfDay(filters.dateTo)?.toISOString(),
      searchTerm: filters.searchText || undefined,
      actions: mappedActions,
      entityTypes: mappedEntityTypes,
      userId: filters.userIds?.length === 1 ? filters.userIds[0] : undefined
    };

    const auditCall$ = isFeatureSelected
      ? this.auditService.getFeatureAuditTrail(options)
      : this.auditService.getDefaultAuditTrail(this.structureId!, options);

    auditCall$.pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result: IAuditTrailResult) => {
        this.auditEntries = result.auditLogs.map(entry => ({
          ...entry,
          actionIcon: this.getActionIcon(entry.action),
          actionColor: this.getActionColorForEntry(entry),
          isExpanded: false
        }));
        this.totalCount = result.totalCount;
        this.applyClientSideFilters();
        this.updateAvailableUsers();
        // Don't update available actions after search - preserve original options
        this.updateAvailableEntityTypes();
        if (isFeatureSelected && result.auditLogs.length > 0) {
          this.applyFeatureColumnOverrides();
        } else if (!isFeatureSelected) {
          this.loadColumnPreferences();
        }
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load audit data:', error);
        this.auditEntries = [];
        this.totalCount = 0;
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private applyClientSideFilters(): void {
    const filters: IAuditFilters = this.filterForm.value;

    this.dataSource.data = this.auditEntries.filter(entry => {
      // User filter (by display name) - only if not already filtered by backend
      if (filters.users?.length && !filters.users.includes(entry.userDisplayName)) return false;

      // Manual changes filter (client-side only)
      if (filters.showSystemChanges) {
        // When checked: show only manual entries (production support manual fixes)
        if (entry.source !== 'SystemUser') return false;
      } else {
        // When unchecked (default): show only Background entries (automatic logging)
        if (entry.source !== 'Background') return false;
      }

      return true;
    });

    this.connectSort(); // Reconnect sort after data update

    // Update quick filter states based on current filters
    this.updateQuickFilterStates();
    this.cdr.markForCheck();
  }

  // UI Event Handlers
  toggleExpanded(entry: IAuditLogEntry): void {
    if (entry.hasDetails) {
      // If clicking on an already expanded row, just collapse it
      if (entry.isExpanded) {
        entry.isExpanded = false;
        this.expandedItems.delete(entry.id);
      } else {
        // Collapse all other expanded rows first
        this.dataSource.data.forEach(e => {
          if (e.isExpanded && e.id !== entry.id) {
            e.isExpanded = false;
            this.expandedItems.delete(e.id);
          }
        });

        // Then expand the clicked row
        entry.isExpanded = true;
        this.expandedItems.add(entry.id);

        // Auto-scroll to show expanded content after animation
        setTimeout(() => {
          this.scrollToExpandedRow(entry.id);
        }, 350); // Wait for expand animation to complete
      }
      this.cdr.markForCheck();
    }
  }

  private scrollToExpandedRow(_entryId: number): void {
    // Find the expanded detail row
    const expandedRow = document.querySelector(`tr.detail-row-expanded`);
    if (expandedRow) {
      // Get the table container and header height
      const tableContainer = document.querySelector('.audit-table-container');
      const headerRow = document.querySelector('.mat-mdc-header-row') as HTMLElement;
      const headerHeight = headerRow?.offsetHeight || 60;
      
      // Calculate optimal scroll position
      const containerRect = tableContainer?.getBoundingClientRect();
      
      if (containerRect) {
        // Check if we need to scroll to keep header visible
        const availableSpace = window.innerHeight - containerRect.top - headerHeight;
        const expandedRowHeight = expandedRow.scrollHeight;
        
        if (expandedRowHeight > availableSpace * 0.7) {
          // Large expanded content - scroll to start with header visible
          expandedRow.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
        } else {
          // Smaller content - use gentle scroll that keeps some context
          expandedRow.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
            inline: 'nearest'
          });
        }
      }
    }
  }

  applyQuickFilter(filterKey: string): void {
    const wasActive = this.quickFilters[filterKey].active;
    
    // Clear all filters first
    Object.keys(this.quickFilters).forEach(key => {
      this.quickFilters[key].active = false;
    });
    
    // Clear form
    this.filterForm.patchValue({
      dateFrom: null,
      dateTo: null,
      users: [],
      userIds: [],
      actions: [],
      entityTypes: [],
      searchText: '',
      showSystemChanges: false
    });
    
    // If the filter wasn't active, activate it and apply its specific filters
    if (!wasActive) {
      this.quickFilters[filterKey].active = true;
      this.applySpecificFilter(filterKey);
    }
    
    // Always reload data (either with new filter or reset to default)
    this.searchAuditData();
  }

  private applySpecificFilter(filterKey: string): void {
    switch (filterKey) {
      case 'ALL_TIME':
        // All Time means no date filters
        this.filterForm.patchValue({
          dateFrom: null,
          dateTo: null
        });
        break;

      case 'WEEK':
        this.filterForm.patchValue({
          dateFrom: this.getWeekAgo(),
          dateTo: null
        });
        break;

      case 'LAST_MONTH':
        this.filterForm.patchValue({
          dateFrom: this.getStartOfLastMonth(),
          dateTo: this.getEndOfLastMonth()
        });
        break;

      case 'MY_CHANGES':
        if (this.currentUserId) {
          this.filterForm.patchValue({
            userIds: [this.currentUserId]
          });
        }
        break;

      case 'CREATES_ONLY':
        this.filterForm.patchValue({
          actions: ['Create']
        });
        break;

      case 'DELETES_ONLY':
        this.filterForm.patchValue({
          actions: ['Delete']
        });
        break;

      case 'UPDATES_ONLY':
        this.filterForm.patchValue({
          actions: ['Update']
        });
        break;

      default:
        console.warn(`Unknown filter key: ${filterKey}`);
        break;
    }
  }


  clearFilters(): void {
    // Clear each control individually for better mat-select clearing
    this.filterForm.get('dateFrom')?.setValue(null);
    this.filterForm.get('dateTo')?.setValue(null);
    this.filterForm.get('users')?.setValue([]);
    this.filterForm.get('userIds')?.setValue([]);
    this.filterForm.get('actions')?.setValue([]);
    this.filterForm.get('entityTypes')?.setValue([]);
    this.filterForm.get('searchText')?.setValue('');
    this.filterForm.get('showSystemChanges')?.setValue(false);

    // Mark form as pristine after clearing
    this.filterForm.markAsPristine();
    this.filterForm.markAsUntouched();

    // Deactivate all chips except All Time
    Object.keys(this.quickFilters).forEach(key => {
      if (key === 'ALL_TIME') {
        this.quickFilters[key].active = true;
      } else if (this.quickFilters[key].active) {
        this.quickFilters[key].active = false;
      }
    });

    // Reset filtered entries to show all data
    this.dataSource.data = [...this.auditEntries];
    this.connectSort(); // Reconnect sort after data update
    this.cdr.markForCheck();
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
    // Close column selector if opening filters
    if (this.showAdvancedFilters && this.showColumnSelector) {
      this.showColumnSelector = false;
    }
    this.cdr.markForCheck();
  }

  toggleSystemChanges(): void {
    const currentValue = this.filterForm.get('showSystemChanges')?.value;
    this.filterForm.patchValue({ showSystemChanges: !currentValue });
    this.applyClientSideFilters();
  }

  onPageChange(event: any): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    // Call loadAuditDataWithFilters directly to avoid resetting currentPage
    this.loadAuditDataWithFilters();
  }


  // Entity type mapping utility
  private mapEntityTypeToBackend(displayName: string): string {
    const entityTypeMap: { [key: string]: string } = {
      'Key': 'MasterKey',
      'Cylinder': 'MasterKeyCylinder', 
      'Book': 'Combination',
      'Key System': 'Key System',
      'Structure': 'Structure'
    };
    
    return entityTypeMap[displayName] || displayName;
  }

  getEntityDisplayName(entry: IAuditLogEntry): string {
    if (entry.entityType === 'Feature' && entry.entityDisplayName?.startsWith('Feature: ')) {
      return entry.entityDisplayName.slice('Feature: '.length).replace(/_/g, ' ');
    }
    return entry.entityDisplayName;
  }

  mapEntityTypeToDisplay(backendType: string): string {
    const displayTypeMap: { [key: string]: string } = {
      'MasterKey': 'Key',
      'MasterKeyCylinder': 'Cylinder',
      'Combination': 'Book',
      'Key System': 'Key System',
      'Structure': 'Structure'
    };

    return displayTypeMap[backendType] || backendType;
  }

  // Action type mapping utility
  private mapActionToBackend(displayName: string): string {
    const actionTypeMap: { [key: string]: string } = {
      'Create': 'CREATE',
      'Update': 'UPDATE',
      'Delete': 'DELETE'
    };

    return actionTypeMap[displayName] || displayName.toUpperCase();
  }

  // Utility methods
  getActionIcon(action: string): string {
    // Normalize to uppercase for mapping
    const normalizedAction = action?.toUpperCase();
    const iconMap: { [key: string]: string } = {
      'CREATE': 'add_circle',
      'UPDATE': 'edit',
      'UPGRADE': 'upgrade',
      'DELETE': 'delete',
      'STATUS_CHANGE': 'swap_horiz',
      'PUBLISH': 'publish',
      'IMPORT': 'file_upload',
      'EXPORT': 'file_download',
      'OVERRIDE': 'info'
    };
    return iconMap[normalizedAction] || 'info';
  }

  getActionColor(action: string): string {
    // Normalize to uppercase for mapping
    const normalizedAction = action?.toUpperCase();
    const colorMap: { [key: string]: string } = {
      'CREATE': '#4CAF50',
      'UPDATE': '#2196F3',
      'UPGRADE': '#2196F3', // Same color as UPDATE since they're both modifications
      'DELETE': '#F44336',
      'STATUS_CHANGE': '#FF9800',
      'PUBLISH': '#9C27B0',
      'IMPORT': '#607D8B',
      'EXPORT': '#795548',
      'OVERRIDE': '#FF9800' // Orange - same as View Errors button
    };
    return colorMap[normalizedAction] || '#757575';
  }

  /**
   * Get action color for an entry, checking for PUBLISH with MECSErrors (override)
   */
  getActionColorForEntry(entry: IAuditLogEntry): string {
    const normalizedAction = entry.action?.toUpperCase();

    // If PUBLISH action has MECSErrors in details, show orange (override was involved)
    if (normalizedAction === 'PUBLISH' && entry.details?.some(d => d.fieldName === 'MECSErrors')) {
      return '#FF9800'; // Orange - same as OVERRIDE
    }

    return this.getActionColor(entry.action);
  }

  getTimeAgo(timestamp: string): string {
    const now = new Date();
    const diffMs = now.getTime() - new Date(timestamp).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return new Date(timestamp).toLocaleDateString();
  }

  getFieldChangeText(detail: IAuditLogDetail): string {
    if (detail.isSecured) {
      return `${detail.fieldDisplayName}: ***SECURED***`;
    }
    
    const oldVal = detail.oldDisplayValue || '[Empty]';
    const newVal = detail.newDisplayValue || '[Empty]';
    
    if (!detail.oldDisplayValue && detail.newDisplayValue) {
      return `${detail.fieldDisplayName}: Set to "${newVal}"`;
    }
    
    if (detail.oldDisplayValue && !detail.newDisplayValue) {
      return `${detail.fieldDisplayName}: Cleared from "${oldVal}"`;
    }
    
    return `${detail.fieldDisplayName}: "${oldVal}" → "${newVal}"`;
  }

  // Track by functions for ngFor optimization
  trackByDetailId(_index: number, detail: IAuditLogDetail): number {
    return detail.id;
  }

  trackByEntryId(_index: number, entry: IAuditLogEntry): number {
    return entry.id;
  }

  // Column visibility management
  toggleColumnSelector(): void {
    this.showColumnSelector = !this.showColumnSelector;
    // Close advanced filters if opening column selector
    if (this.showColumnSelector && this.showAdvancedFilters) {
      this.showAdvancedFilters = false;
    }
    this.cdr.markForCheck();
  }

  toggleColumnVisibility(columnKey: string): void {
    const column = this.allColumns.find(col => col.key === columnKey);
    if (column && !column.required) {
      column.visible = !column.visible;
      this.saveColumnPreferences();
      this.cdr.markForCheck();
    }
  }

  resetColumnsToDefault(): void {
    this.resetToInitialDefaults();
    this.saveColumnPreferences();
    this.cdr.markForCheck();
  }

  private loadColumnPreferences(): void {
    const saved = localStorage.getItem('audit-trail-columns');
    if (saved) {
      try {
        const preferences = JSON.parse(saved);
        this.allColumns.forEach(col => {
          const savedCol = preferences.find((p: any) => p.key === col.key);
          if (savedCol !== undefined) {
            col.visible = savedCol.visible;
          }
        });
      } catch (error) {
        console.warn('Failed to load column preferences:', error);
        // Reset to defaults on error
        this.resetToInitialDefaults();
      }
    } else {
      // No saved preferences - ensure defaults are applied
      this.resetToInitialDefaults();
    }
  }

  private applyFeatureColumnOverrides(): void {
    this.allColumns.forEach(col => {
      if (col.key === 'identifier' || col.key === 'keyway') {
        col.visible = false;
      }
    });
  }

  private resetToInitialDefaults(): void {
    this.allColumns.forEach(col => {
      // Set initial defaults - hide Source only
      if (col.key === 'source') {
        col.visible = false;
      } else if (col.required) {
        col.visible = true;
      } else {
        col.visible = true; // Show other optional columns by default
      }
    });
  }

  private saveColumnPreferences(): void {
    const preferences = this.allColumns.map(col => ({
      key: col.key,
      visible: col.visible
    }));
    localStorage.setItem('audit-trail-columns', JSON.stringify(preferences));
  }

  /**
   * Get filtered details for display - show detailed field table for all actions
   * Filter out rows where both old and new values are empty/null, contain reference values, or are EmptyChambers
   */
  getFilteredDetails(entry: IAuditLogEntry): IAuditLogDetail[] {
    if (!entry.details) return [];

    // Filter out details where both old and new values are empty/null or contain reference values
    const filteredDetails = entry.details.filter(detail => {
      const hasOldValue = detail.oldValue && detail.oldValue.trim() !== '';
      const hasNewValue = detail.newValue && detail.newValue.trim() !== '';

      // Hide EmptyChambers field since it's now visually represented in ShearLines
      if (detail.fieldName === 'EmptyChambers') {
        return false;
      }

      // Hide specific fields
      const hiddenFields = ['Number', 'Prefix', 'Budget', 'Change Key Expansion'];
      if (hiddenFields.includes(detail.fieldDisplayName)) {
        return false;
      }

      // Hide rows where either old or new value starts with "Reference"
      const oldValueIsReference = detail.oldDisplayValue && detail.oldDisplayValue.toLowerCase().startsWith('reference');
      const newValueIsReference = detail.newDisplayValue && detail.newDisplayValue.toLowerCase().startsWith('reference');

      if (oldValueIsReference || newValueIsReference) {
        return false;
      }

      // Show only if there's at least one non-empty value (showing actual changes)
      if (!hasOldValue && !hasNewValue) {
        return false;
      }

      // For boolean fields, hide if transformed values would be identical (no actual change)
      if (this.isBooleanField(detail)) {
        const transformedOld = this.getDisplayValue(detail.oldDisplayValue, detail);
        const transformedNew = this.getDisplayValue(detail.newDisplayValue, detail);
        if (transformedOld === transformedNew) {
          return false;
        }
      }

      return true;
    });

    return filteredDetails.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  /**
   * Get comma-separated field names for CREATE operations
   */
  getCreatedFieldsSummary(entry: IAuditLogEntry): string {
    if (!entry.details || entry.action !== 'CREATE') return '';
    
    const fieldNames = entry.details
      .filter(detail => detail.newValue && detail.newValue.trim() !== '')
      .map(detail => detail.fieldDisplayName)
      .join(', ');
    
    return fieldNames;
  }

  /**
   * Get comma-separated field values for CREATE operations
   */
  getCreatedValuesSummary(entry: IAuditLogEntry): string {
    if (!entry.details || entry.action !== 'CREATE') return '';

    const fieldValues = entry.details
      .filter(detail => detail.newValue && detail.newValue.trim() !== '')
      .map(detail => detail.newDisplayValue || detail.newValue)
      .join(', ');

    return fieldValues;
  }

  /**
   * Format ShearLines values for display as individual character arrays
   * Handles both single values and semicolon-separated multiple values
   */
  formatShearLinesValue(value: string): string[][] {
    if (!value || value.trim() === '') return [];

    // If value contains semicolons, split into multiple rows
    if (value.includes(';')) {
      return value.split(';')
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(line => line.split(''));
    }

    // Single value - return as single row array
    return [value.split('')];
  }

  /**
   * Check if a field is ShearLines field that needs special formatting
   */
  isShearLinesField(fieldName: string): boolean {
    return fieldName === 'ShearLines';
  }

  /**
   * Check if a field is Key Bit Array field that needs special formatting
   */
  isKeyBitArrayField(fieldDisplayName: string): boolean {
    return fieldDisplayName === 'Key Bit Array';
  }

  /**
   * Check if a field contains user target values (array of JSON user objects)
   */
  isUserTargetValuesField(fieldName: string): boolean {
    return fieldName === 'TargetValues[user]';
  }

  /**
   * Check if a field contains role target values (plain JSON string array)
   */
  isRoleTargetValuesField(fieldName: string): boolean {
    return fieldName === 'TargetValues[role]';
  }

  /**
   * Parse and format a plain JSON string array (e.g. ["SuperUser"]) into a comma-separated string
   */
  formatRoleTargetValues(value: string): string {
    if (!value || value.trim() === '') return '';
    try {
      const items: string[] = JSON.parse(value);
      return items.join(', ');
    } catch {
      return value;
    }
  }

  /**
   * Extract and format email addresses from user target values JSON string
   */
  formatUserTargetValues(value: string): string {
    if (!value || value.trim() === '') return '';
    try {
      const items: string[] = JSON.parse(value);
      return items
        .map(item => {
          try {
            const parsed = JSON.parse(item);
            return parsed.email || item;
          } catch {
            return item;
          }
        })
        .join(', ');
    } catch {
      return value;
    }
  }

  /**
   * Check if a field is MECS Errors field that needs dialog display
   */
  isMecsErrorsField(fieldName: string): boolean {
    return fieldName === 'MECSErrors';
  }

  /**
   * Open dialog to display MECS errors in a formatted table
   */
  openMecsErrorsDialog(errorString: string, event: Event): void {
    event.stopPropagation(); // Prevent row expansion when clicking icon

    this.dialog.open(MecsErrorsDialogComponent, {
      data: {
        title: 'MECS Errors',
        errorString: errorString
      },
      width: '90vw',
      maxWidth: '1200px',
      maxHeight: '80vh'
    });
  }

  /**
   * Format Key Bit Array value for display - split by colon into separate lines
   * and split each line into individual characters for spacing
   */
  formatKeyBitArrayValue(value: string): string[][] {
    if (!value || value.trim() === '') return [];

    return value.split(':')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => item.split(''));
  }

  /**
   * Check if a column (character position) should be grayed out
   * Uses EmptyChambers field - both ShearLines and EmptyChambers use zero-based indexing
   */
  isColumnGrayedOut(entry: IAuditLogEntry, columnIndex: number, isOldValue: boolean): boolean {
    // Try to find EmptyChambers field in the same entry
    const emptyChambers = entry.details?.find(detail => detail.fieldName === 'EmptyChambers');

    if (emptyChambers) {
      const chambersValue = isOldValue ? emptyChambers.oldDisplayValue : emptyChambers.newDisplayValue;

      if (chambersValue && chambersValue.trim() !== '') {
        // Parse EmptyChambers (zero-indexed) - direct comparison with charIndex
        const emptyIndices = chambersValue.split(';')
          .map(index => parseInt(index.trim(), 10))
          .filter(index => !isNaN(index));

        // Column is grayed out if its zero-based position matches empty chambers
        return emptyIndices.includes(columnIndex);
      }
    }

    return false;
  }

  /**
   * Transform field display names based on configuration map
   * Add new transformations to FIELD_DISPLAY_NAME_MAP constant
   */
  transformFieldDisplayName(fieldDisplayName: string): string {
    return this.FIELD_DISPLAY_NAME_MAP[fieldDisplayName] || fieldDisplayName;
  }

  /**
   * Check if a field is a boolean type based on its values
   */
  isBooleanField(detail: IAuditLogDetail): boolean {
    const booleanValues = ['yes', 'no', 'true', 'false'];
    const oldValue = detail.oldDisplayValue?.toLowerCase();
    const newValue = detail.newDisplayValue?.toLowerCase();

    return (oldValue && booleanValues.includes(oldValue)) ||
           (newValue && booleanValues.includes(newValue));
  }

  /**
   * Get display value for a field, handling boolean fields specially
   * For boolean fields, empty/null values should display as "No" instead of "[Empty]"
   */
  getDisplayValue(value: string | null | undefined, detail: IAuditLogDetail): string {
    if (!value || value.trim() === '') {
      return this.isBooleanField(detail) ? 'No' : '[Empty]';
    }
    return value;
  }

}